import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ArrowUpRight, ArrowDownRight, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Granularity } from "@/lib/periods";
import type { FilterState, DrillLevel, ScopeFilters } from "@/types/filters";
import type { SchedulingRow } from "@/types/scheduling";
import { useRevenueData, type RevenueDrillRow } from "@/hooks/useRevenueData";
import { useRollupRevenue } from "@/hooks/useRollupRevenue";
import { comparePaths } from "@/lib/rollupCompare";
import type { DataPath } from "@/lib/rollupFlag";
import type { RollupPayload } from "@/hooks/useRollupData";
import { useRevenueTrendData } from "@/hooks/useRevenueTrendData";
import { useRollupRevenueTrend } from "@/hooks/useRollupRevenueTrend";
import { useRollupData } from "@/hooks/useRollupData";
import { needsRollup } from "@/lib/rollupFlag";
import { Card } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { KpiCard } from "@/components/dashboard/KpiCard";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { METRIC_TOOLTIPS, type MetricTooltipKey } from "@/lib/metricTooltips";
import { selectedDrillId } from "@/lib/drillScope";
import { RevenueTrendChart } from "@/components/dashboard/RevenueTrendChart";
import { RevenueMetricChart } from "@/components/dashboard/RevenueMetricChart";

const DRILL_LEVELS: { key: DrillLevel; label: string }[] = [
  { key: "country", label: "Country" },
  { key: "group", label: "Group" },
  { key: "division", label: "Division" },
  { key: "site", label: "Site" },
  { key: "dvm", label: "DVM" },
];

const TOGGLE_ITEM_CLS =
  "h-auto text-[12px] px-3 py-1 border-black/15 bg-white text-gray-600 font-normal hover:bg-gray-50 hover:text-gray-600 transition-colors data-[state=on]:bg-[var(--brand-dark-teal)] data-[state=on]:text-white data-[state=on]:border-[var(--brand-dark-teal)]";

function formatDollars(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

// vs. PY / vs. NVA delta rows only -- the headline figure stays exact, because
// it comes from displayValue rather than this.
//
// Abbreviating here is what makes five cards fit in one row. At five columns the
// grid caps each card at 230px and the vs-PY row on Med. Net Revenue per DVM
// needed 224px against the 197px it gets, so "$8,623 +$1,317" rendered 39px
// outside its own card. "$8.6k +$1.3k" fits with room to spare.
//
// Only values in the thousands change: $566 and $355 are already short enough,
// so the rate cards read exactly as before.
function formatDollarsCompact(value: number): string {
  const trim = (n: number) => n.toFixed(1).replace(/\.0$/, "");
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${trim(value / 1_000_000)}M`;
  if (abs >= 1_000) return `$${trim(value / 1_000)}k`;
  return formatDollars(value);
}

const formatDollarDelta = (n: number) => formatDollarsCompact(n);

// Fields shared by both RevenueDrillRow and RevenuePinnedRow — these helpers
// work on either, since a pinned ancestor row is rendered with the same
// per-metric formulas as a regular row.
interface RevenueMetricFields {
  dvmDays: number;
  scheduledMinutes: number;
  apptMinutes: number;
  completedAppts: number;
  medicalRevenue: number;
  pyMedicalRevenue: number | null;
}

function revPerSchedHour(row: RevenueMetricFields): number {
  return row.scheduledMinutes > 0 ? row.medicalRevenue / (row.scheduledMinutes / 60) : 0;
}
function revPerSeenHour(row: RevenueMetricFields): number {
  return row.apptMinutes > 0 ? row.medicalRevenue / (row.apptMinutes / 60) : 0;
}
function avgApptMin(row: RevenueMetricFields): number {
  return row.completedAppts > 0 ? row.apptMinutes / row.completedAppts : 0;
}
function revPerAppt(row: RevenueMetricFields): number {
  return row.completedAppts > 0 ? row.medicalRevenue / row.completedAppts : 0;
}
// Doctor-DAYS, not calendar days. At Site level and above the two differ by the
// number of doctors involved -- a site with five doctors over a week has 5
// calendar days and 25 doctor-days -- so this reads as "what one doctor gets
// through in a day" at every drill level, and matches the KPI card above the
// table. scheduledDays is the calendar count and is deliberately NOT used here.
function apptsPerDvmDay(row: RevenueMetricFields): number {
  return row.dvmDays > 0 ? row.completedAppts / row.dvmDays : 0;
}
// vs. PY compares Total med. rev and nothing else, which is why the column sits
// immediately beside it rather than at the far end of the row.
function pyDelta(row: { medicalRevenue: number; pyMedicalRevenue: number | null }): number | null {
  if (row.pyMedicalRevenue === null || row.pyMedicalRevenue <= 0) return null;
  return (row.medicalRevenue - row.pyMedicalRevenue) / row.pyMedicalRevenue;
}

function TrendVsPY({ row }: { row: { medicalRevenue: number; pyMedicalRevenue: number | null } }) {
  const delta = pyDelta(row);
  if (delta === null) return <span className="text-gray-300">—</span>;
  const up = delta >= 0;
  const color = up ? "var(--perf-green)" : "var(--perf-red)";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-0.5 font-medium tabular-nums" style={{ color }}>
      <Icon className="h-3.5 w-3.5" />
      {up ? "+" : ""}
      {(delta * 100).toFixed(1)}%
    </span>
  );
}

type SortKey =
  | "revenue"
  | "revPerSeenHour"
  | "revPerSchedHour"
  | "completedAppts"
  | "apptsPerDvmDay"
  | "avgApptMin"
  | "revPerAppt"
  | "vsPy";
type SortDir = "asc" | "desc";

// ONE definition per column: its width, its header, how a cell renders, and how
// it exports. The colgroup, the header row, the data rows, the pinned ancestor
// rows and the CSV all read this list.
//
// They used to be five hand-written sequences that had to stay in the same
// order by hand -- and a pinned row's cells were a separate copy of the data
// row's. Inserting a column meant editing all five correctly or silently
// shifting every value one cell to the left in whichever one was missed.
//
// Widths are sized to the widest realistic content: "Completed Appts" needs room
// for its header, "vs. PY" only for "+378.8%".
const METRIC_COLS: Array<{
  label: string;
  key: SortKey;
  width: number;
  emphasis?: boolean;
  // Hover definition. Absent on Total med. rev, vs. PY, Completed Appts and
  // Avg Appt Time, which were left bare deliberately.
  tip?: MetricTooltipKey;
  render: (row: RevenueMetricFields) => ReactNode;
  csv: (row: RevenueMetricFields) => string;
}> = [
  {
    label: "Total med. rev",
    key: "revenue",
    width: 116,
    emphasis: true,
    render: (r) => formatDollars(r.medicalRevenue),
    csv: (r) => r.medicalRevenue.toFixed(2),
  },
  // Immediately after the figure it compares. It measures Total med. rev against
  // the prior year and nothing else, which was not readable from the far end of
  // an eight-column row.
  {
    label: "vs. PY",
    key: "vsPy",
    width: 86,
    render: (r) => <TrendVsPY row={r} />,
    csv: (r) => (pyDelta(r) ?? "").toString(),
  },
  {
    label: "$/seen hr",
    tip: "revPerSeenHour",
    key: "revPerSeenHour",
    width: 88,
    render: (r) => formatDollars(revPerSeenHour(r)),
    csv: (r) => revPerSeenHour(r).toFixed(2),
  },
  {
    label: "$/sched hr",
    tip: "revPerScheduledHour",
    key: "revPerSchedHour",
    width: 92,
    render: (r) => formatDollars(revPerSchedHour(r)),
    csv: (r) => revPerSchedHour(r).toFixed(2),
  },
  {
    label: "Completed Appts",
    key: "completedAppts",
    width: 126,
    render: (r) => r.completedAppts.toLocaleString(),
    csv: (r) => r.completedAppts.toString(),
  },
  {
    label: "Appts/DVM/day",
    tip: "completedApptsPerDvmPerDay",
    key: "apptsPerDvmDay",
    width: 118,
    render: (r) => (r.dvmDays > 0 ? apptsPerDvmDay(r).toFixed(1) : "—"),
    csv: (r) => apptsPerDvmDay(r).toFixed(2),
  },
  {
    label: "Avg Appt Time",
    key: "avgApptMin",
    width: 112,
    render: (r) => (avgApptMin(r) > 0 ? `${Math.round(avgApptMin(r))} min` : "—"),
    csv: (r) => avgApptMin(r).toFixed(2),
  },
  {
    label: "$/appt",
    tip: "revPerCompletedAppt",
    key: "revPerAppt",
    width: 82,
    render: (r) => (r.completedAppts > 0 ? formatDollars(revPerAppt(r)) : "—"),
    csv: (r) => revPerAppt(r).toFixed(2),
  },
];

function SortArrow({
  colKey,
  activeKey,
  dir,
}: {
  colKey: SortKey | "label";
  activeKey: SortKey | "label";
  dir: SortDir;
}) {
  if (activeKey !== colKey) return <span className="opacity-20 ml-0.5 text-[9px]">↕</span>;
  return (
    <span className="ml-0.5 text-[9px]" style={{ color: "var(--brand-dark-teal)" }}>
      {dir === "asc" ? "↑" : "↓"}
    </span>
  );
}

function sortValue(row: RevenueDrillRow, key: SortKey): number {
  switch (key) {
    case "revenue":
      return row.medicalRevenue;
    case "revPerSeenHour":
      return revPerSeenHour(row);
    case "revPerSchedHour":
      return revPerSchedHour(row);
    case "completedAppts":
      return row.completedAppts;
    case "apptsPerDvmDay":
      return apptsPerDvmDay(row);
    case "avgApptMin":
      return avgApptMin(row);
    case "revPerAppt":
      return revPerAppt(row);
    case "vsPy":
      return pyDelta(row) ?? -Infinity;
  }
}

interface RevenueTabProps {
  rows: SchedulingRow[];
  filters: FilterState;
  startDate: string;
  endDate: string;
  setRevGranularity: (g: FilterState["revGranularity"]) => void;
  setRevMetricGranularity: (g: FilterState["revMetricGranularity"]) => void;
  availableGranularities: Granularity[];
  // Whether the active period is calendar-based; with week grouping that means
  // the part-weeks at each end are left out, which each chart says for itself.
  calendarPeriod: boolean;
  onRowClick: (patch: Partial<ScopeFilters>) => void;
  // Lifted, because it decides whether the rollup request must ask for wide
  // doctor rows -- per-doctor measures live only there.
  drillLevel: DrillLevel;
  setDrillLevel: (l: DrillLevel) => void;
  // Both paths are handed in rather than the tab choosing: the flag lives in one
  // place, and compare mode needs both results side by side.
  rollupPayload: RollupPayload | null;
  dataPath: DataPath;
}

export function RevenueTab({
  rows,
  filters,
  startDate,
  endDate,
  setRevGranularity,
  setRevMetricGranularity,
  availableGranularities,
  calendarPeriod,
  onRowClick,
  drillLevel,
  setDrillLevel,
  rollupPayload,
  dataPath,
}: RevenueTabProps) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "revenue",
    dir: "desc",
  });

  const rawRevenue = useRevenueData(rows, filters, startDate, endDate, drillLevel);
  // The tab keeps its own drill level, so the rollup hook is given THAT one --
  // and the payload was fetched knowing it, which is what puts the wide doctor
  // rows in it. Without them every per-doctor figure here would read zero.
  const rollupRevenue = useRollupRevenue(
    rollupPayload,
    { ...filters, drillLevel },
    filters.capGranularity
  );

  const { kpis, drillRows, pinned } =
    dataPath === "rollup" && rollupRevenue ? rollupRevenue : rawRevenue;
  // One bucketing per chart, so the two Group by toggles are independent.
  const rawTrend = useRevenueTrendData(rows, filters, startDate, endDate, filters.revGranularity);
  const rawMetricTrend = useRevenueTrendData(
    rows,
    filters,
    startDate,
    endDate,
    filters.revMetricGranularity
  );

  // Each chart needs a payload fetched at ITS OWN granularity -- a payload
  // planned for Month withholds the month-straddling weeks as raw days, and asked
  // for a Week series it drops them. Same constraint the Utilization charts have;
  // see useRollupTrend. React Query collapses these into the KPI payload's request
  // whenever the granularities coincide, which is the default.
  //
  // Fetched here rather than in AppPage so they exist only while this tab is
  // mounted. The drill level is this tab's own, matching useRollupRevenue, so the
  // keys line up instead of forking a second payload per grain.
  const trendFilters = { ...filters, drillLevel };
  const { payload: trendPayload } = useRollupData(
    trendFilters,
    filters.revGranularity,
    needsRollup(dataPath)
  );
  const { payload: metricTrendPayload } = useRollupData(
    trendFilters,
    filters.revMetricGranularity,
    needsRollup(dataPath)
  );
  const rollupTrend = useRollupRevenueTrend(trendPayload, trendFilters, filters.revGranularity);
  const rollupMetricTrend = useRollupRevenueTrend(
    metricTrendPayload,
    trendFilters,
    filters.revMetricGranularity
  );

  // The switch every other figure on this tab already had, and these two charts
  // did not: on the rollup path `rows` is empty by design, so reading it
  // unconditionally drew nothing at all.
  const useRollup = dataPath === "rollup";
  const trend = useRollup && rollupTrend ? rollupTrend : rawTrend;
  const metricTrend = useRollup && rollupMetricTrend ? rollupMetricTrend : rawMetricTrend;

  useEffect(() => {
    if (dataPath !== "compare" || !rollupRevenue) return;
    // Scope belongs in the label. comparePaths reports a match once per distinct
    // label, so without it every SCOPED run reused the unscoped one and went
    // silent -- and the scoped case is the one that matters most here, because the
    // NVA benchmark is read unscoped from the same payload the points are read
    // scoped from. "No new line" is not evidence of a check having run; the
    // Utilization tag already carries scope for exactly this reason.
    const sc = filters.scope;
    const scopeTag = [
      sc.countryCode,
      sc.groupName,
      sc.divisionName,
      sc.locationId,
      sc.resourceId,
    ].filter(Boolean).length
      ? [sc.countryCode, sc.groupName, sc.divisionName, sc.locationId, sc.resourceId]
          .map((v) => v ?? "*")
          .join(">")
      : "all";
    const tag = `${filters.period}/${filters.capGranularity}/rev:${drillLevel}/${scopeTag}`;
    comparePaths(`${tag} revenue.kpis`, rawRevenue.kpis, rollupRevenue.kpis);
    comparePaths(`${tag} revenue.pinned`, rawRevenue.pinned, rollupRevenue.pinned);
    // By id, since the two paths need not agree on order among equal revenues.
    const byId = new Map(rollupRevenue.drillRows.map((r) => [r.id, r]));
    comparePaths(
      `${tag} revenue.drill`,
      rawRevenue.drillRows,
      rawRevenue.drillRows.map((r) => byId.get(r.id) ?? null)
    );
    // The charts are diffed too. They were the one surface compare mode never
    // watched, which is exactly why they could be empty on the rollup path
    // without anything reporting it.
    if (rollupTrend) {
      comparePaths(`${tag}/g:${filters.revGranularity} revenue.trend`, rawTrend, rollupTrend);
    }
    if (rollupMetricTrend) {
      comparePaths(
        `${tag}/g:${filters.revMetricGranularity} revenue.metricTrend`,
        rawMetricTrend,
        rollupMetricTrend
      );
    }
  }, [
    dataPath,
    rawRevenue,
    rollupRevenue,
    rawTrend,
    rollupTrend,
    rawMetricTrend,
    rollupMetricTrend,
    filters.period,
    filters.capGranularity,
    filters.revGranularity,
    filters.revMetricGranularity,
    filters.scope,
    drillLevel,
  ]);

  // Rank is fixed to medical revenue regardless of the active sort
  const rankById = new Map(drillRows.map((r, i) => [r.id, i + 1]));

  const sorted = [...drillRows].sort((a, b) => {
    const cmp = sortValue(a, sort.key) - sortValue(b, sort.key);
    return sort.dir === "asc" ? cmp : -cmp;
  });

  // Bottom 20th percentile of Total med. rev among rows currently visible at
  // this drill level — same convention as the Utilization tab's Seen % flag.
  const revThreshold = useMemo(() => {
    if (drillRows.length === 0) return 0;
    const revs = drillRows.map((r) => r.medicalRevenue).sort((a, b) => a - b);
    const idx = Math.max(0, Math.floor(revs.length * 0.2) - 1);
    return revs[idx] ?? 0;
  }, [drillRows]);

  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }
    );
  }

  const selectedId = selectedDrillId(filters.scope, drillLevel);

  function handleRowClick(row: RevenueDrillRow) {
    switch (drillLevel) {
      case "country":
        onRowClick({ countryCode: row.id });
        break;
      case "division":
        onRowClick({ divisionName: row.id });
        break;
      case "group":
        onRowClick({ groupName: row.id });
        break;
      case "site":
        onRowClick({ locationId: row.id });
        break;
      case "dvm":
        onRowClick({ resourceId: row.id });
        break;
    }
  }

  function exportCsv() {
    const header = [entityLabel, ...METRIC_COLS.map((c) => c.label)].join(",");
    const dataRows = sorted.map((r) =>
      [`"${r.label}"`, ...METRIC_COLS.map((c) => c.csv(r))].join(",")
    );
    const csv = [header, ...dataRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fetchtime_revenue_${drillLevel}_${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const entityLabel = DRILL_LEVELS.find((l) => l.key === drillLevel)?.label ?? "";

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-8">
        <Alert className="border-amber-300 bg-amber-50 text-amber-900">
          <TriangleAlert />
          <AlertTitle>Beta Version — Data Validation in Progress</AlertTitle>
          <AlertDescription className="text-amber-800">
            This dashboard is currently in beta. We're still validating the data and refining
            calculations, so some metrics may change or appear inconsistent. Thank you for helping
            us test and improve it.
          </AlertDescription>
        </Alert>

        {/* KPI cards */}
        <div>
          {/* All five in one row, replacing the 2+3 grouping PR #45 introduced.
            The grouping was there because the row did not fit; it fits now
            because the delta rows were made compact, not because the widths
            changed.

            auto-fit against a measured column width rather than breakpoints,
            matching FormulaCardRow. Breakpoints were costing real width here:
            five fit from ~1372px but the nearest stock stop above that is 2xl
            (1536px), so between the two this showed THREE -- and at xl (1280px)
            five would have overlapped, so xl was not available either.
            Arbitrary variants like min-[1400px] are not generated in this setup.
            Deriving the count from the space instead drops the five-across
            threshold to ~1372 and, below it, gives four rather than three.

            208px is the measured minimum: bisecting the column width until a
            delta row overflows puts it at 204px, plus 4px because text metrics
            differ between machines. It only gets that low because the delta rows
            are compact -- "PY $8.6k" rather than "vs. PY $8,623" (see
            KpiCard.compactDeltas). With the full labels and unabbreviated
            baselines it overflowed by 39px at EVERY width, because the content
            column caps at 1200px. */}
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-500 mb-2">
            Medical Net Revenue
          </h2>
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(208px, 1fr))" }}
          >
            <KpiCard
              label="Med. Net Revenue per DVM"
              unit=""
              current={kpis.medRevPerDvm.current}
              priorYear={kpis.medRevPerDvm.priorYear}
              nva={kpis.medRevPerDvm.nva}
              deltaUnit=""
              compactDeltas
              definition={METRIC_TOOLTIPS.medNetRevenue}
              displayValue={formatDollars(kpis.medRevPerDvm.current)}
              formatValue={formatDollarDelta}
            />
            <KpiCard
              label="Per Pet Seen Hour"
              unit=""
              current={kpis.medRevPerSeenHour.current}
              priorYear={kpis.medRevPerSeenHour.priorYear}
              nva={kpis.medRevPerSeenHour.nva}
              deltaUnit=""
              compactDeltas
              definition={METRIC_TOOLTIPS.revPerSeenHour}
              displayValue={formatDollars(kpis.medRevPerSeenHour.current)}
              formatValue={formatDollarDelta}
            />
            <KpiCard
              label="Per Scheduled Hour"
              unit=""
              current={kpis.medRevPerSchedHour.current}
              priorYear={kpis.medRevPerSchedHour.priorYear}
              nva={kpis.medRevPerSchedHour.nva}
              deltaUnit=""
              compactDeltas
              definition={METRIC_TOOLTIPS.revPerScheduledHour}
              displayValue={formatDollars(kpis.medRevPerSchedHour.current)}
              formatValue={formatDollarDelta}
            />
            <KpiCard
              label="Per Completed Appt."
              unit=""
              current={kpis.medRevPerAppt.current}
              priorYear={kpis.medRevPerAppt.priorYear}
              nva={kpis.medRevPerAppt.nva}
              deltaUnit=""
              compactDeltas
              definition={METRIC_TOOLTIPS.revPerCompletedAppt}
              displayValue={formatDollars(kpis.medRevPerAppt.current)}
              formatValue={formatDollarDelta}
            />
            {/* A count, not a currency -- KpiCard's default one-decimal rendering
              is the right one here, so no displayValue or formatValue. */}
            <KpiCard
              label="Completed Appts. per DVM per Day"
              unit=""
              current={kpis.apptsPerDvmDay.current}
              priorYear={kpis.apptsPerDvmDay.priorYear}
              nva={kpis.apptsPerDvmDay.nva}
              deltaUnit=""
              compactDeltas
              definition={METRIC_TOOLTIPS.completedApptsPerDvmPerDay}
            />
          </div>
        </div>

        {/* Trend charts.
          A GRID, matching the Utilization and Capacity trends -- not a flex row.
          Tailwind's grid-cols-2 emits minmax(0, 1fr), so a column may shrink
          below its content's intrinsic width; a flex item defaults to
          min-width:auto and refuses to, which is why these two charts overflowed
          the page on a narrow window while the other two never did. */}
        <div className="grid grid-cols-2 gap-4">
          <RevenueTrendChart
            trend={trend}
            granularity={filters.revGranularity}
            availableGranularities={availableGranularities}
            partialWeeksShown={calendarPeriod && filters.revGranularity === "week"}
            setGranularity={setRevGranularity}
          />
          <RevenueMetricChart
            trend={metricTrend}
            granularity={filters.revMetricGranularity}
            availableGranularities={availableGranularities}
            partialWeeksShown={calendarPeriod && filters.revMetricGranularity === "week"}
            setGranularity={setRevMetricGranularity}
          />
        </div>

        {/* Revenue drill-down */}
        <Card className="px-5 py-4 gap-0 shadow-none border-black/10">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="text-[16px] font-medium">Revenue Drill-Down</div>
              <button
                onClick={exportCsv}
                className="text-[12px] px-3 py-1 border border-black/15 rounded bg-white text-gray-500 hover:bg-gray-50 transition-colors"
              >
                Export CSV
              </button>
            </div>
            <ToggleGroup
              type="single"
              variant="outline"
              value={drillLevel}
              onValueChange={(v) => {
                if (v) setDrillLevel(v as DrillLevel);
              }}
            >
              {DRILL_LEVELS.map((l) => (
                <ToggleGroupItem key={l.key} value={l.key} className={TOGGLE_ITEM_CLS}>
                  {l.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          <p className="text-[12px] text-gray-400 mb-3">
            Click a column header to sort · Click a row to filter the dashboard · Shift + scroll to
            scroll horizontally
          </p>

          <ScrollArea className="h-[560px] [&_[data-slot=table-container]]:overflow-visible">
            <Table className="text-[13px] table-fixed">
              <colgroup>
                <col style={{ width: "auto", minWidth: "160px", maxWidth: "260px" }} />
                <col style={{ width: "40px" }} />
                {METRIC_COLS.map((c) => (
                  <col key={c.key} style={{ width: `${c.width}px` }} />
                ))}
              </colgroup>
              <TableHeader>
                <TableRow className="border-b border-black/10">
                  <TableHead
                    onClick={() => toggleSort("revenue")}
                    className="px-2 h-[40px] py-0 text-left text-gray-500 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors whitespace-nowrap bg-white sticky top-0 z-[3]"
                  >
                    {entityLabel}
                  </TableHead>
                  <TableHead className="px-2 h-[40px] py-0 text-right text-gray-400 font-medium whitespace-nowrap bg-white sticky top-0 z-[3]">
                    #
                  </TableHead>
                  {METRIC_COLS.map(({ label, key, tip }) => (
                    <TableHead
                      key={key}
                      onClick={() => toggleSort(key)}
                      className="px-2 h-[40px] py-0 text-right text-gray-500 font-medium cursor-pointer select-none hover:text-gray-800 transition-colors whitespace-nowrap bg-white sticky top-0 z-[3]"
                    >
                      {/* On the label rather than the cell -- the cell is the
                        sort target and spans the whole column. */}
                      {tip ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="decoration-dotted underline-offset-2 hover:underline">
                              {label}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            showArrow={false}
                            className="max-w-[280px] border border-black/10 bg-white text-left font-normal text-gray-600 shadow-lg"
                          >
                            {METRIC_TOOLTIPS[tip]}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        label
                      )}
                      <SortArrow colKey={key} activeKey={sort.key} dir={sort.dir} />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* Pinned ancestor rows */}
                {pinned.map((row, i) => {
                  const pinnedCell = { background: "var(--brand-cream)", top: `${41 + i * 39}px` };
                  return (
                    <TableRow key={`pinned-${i}`} className="border-b border-black/5">
                      <TableCell
                        className="px-2 py-2.5 text-left text-[12px] italic text-gray-500 font-medium truncate sticky z-[2]"
                        style={pinnedCell}
                      >
                        {row.label}
                      </TableCell>
                      <TableCell
                        className="px-2 py-2.5 text-right text-[12px] italic text-gray-500 sticky z-[2]"
                        style={pinnedCell}
                      />
                      {METRIC_COLS.map((c) => (
                        <TableCell
                          key={c.key}
                          className="px-2 py-2.5 text-right text-[12px] italic text-gray-500 sticky z-[2] tabular-nums"
                          style={pinnedCell}
                        >
                          {c.render(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}

                {/* Data rows */}
                {sorted.map((row) => {
                  const flagged = row.medicalRevenue <= revThreshold;
                  const rowStyle = flagged ? { color: "var(--perf-red-dark)" } : undefined;
                  // The row for the current selection at this level -- the list is
                  // no longer narrowed to it. Kept as a background tint only, so it
                  // does not fight the red the revenue flag puts on the text.
                  const isSelected = row.id === selectedId;
                  return (
                    <TableRow
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      aria-current={isSelected ? "true" : undefined}
                      className={[
                        "border-b border-black/5 last:border-0 cursor-pointer transition-colors",
                        isSelected
                          ? "bg-[color-mix(in_srgb,var(--brand-light-teal)_30%,white)] hover:bg-[color-mix(in_srgb,var(--brand-light-teal)_38%,white)]"
                          : "hover:bg-[color-mix(in_srgb,var(--brand-cream)_40%,white)]",
                      ].join(" ")}
                      style={rowStyle}
                    >
                      <TableCell
                        className={[
                          "px-2 py-2.5 text-left truncate",
                          isSelected ? "font-semibold" : "font-medium",
                        ].join(" ")}
                      >
                        {row.label}
                        {row.subLabel && (
                          <div
                            className={
                              flagged
                                ? "text-[11px] font-normal"
                                : "text-[11px] font-normal text-gray-400"
                            }
                          >
                            {row.subLabel}
                          </div>
                        )}
                      </TableCell>
                      <TableCell
                        className="px-2 py-2.5 text-right tabular-nums"
                        style={{ color: flagged ? undefined : "#9ca3af" }}
                      >
                        #{rankById.get(row.id)}
                      </TableCell>
                      {METRIC_COLS.map((c) => (
                        <TableCell
                          key={c.key}
                          className={
                            c.emphasis
                              ? "px-2 py-2.5 text-right font-medium tabular-nums"
                              : "px-2 py-2.5 text-right tabular-nums"
                          }
                        >
                          {c.render(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}

                {sorted.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={2 + METRIC_COLS.length}
                      className="py-8 text-center text-gray-400 text-[13px]"
                    >
                      No data for the selected filters
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </Card>
      </div>
    </TooltipProvider>
  );
}
